import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { Link, useParams } from 'react-router-dom';
import { GET_MEETINGS_MINIMAL } from '../../graphql/queries/meetingsMinimal';
import { GET_DAILY_BEST_BETS } from '../../graphql/queries/dailyBestBets';
import { GET_ACTIVE_PROMOTION } from '../../graphql/queries/promotions';
import { GET_UNRELEASED_MEETINGS } from '../../graphql/queries/unreleasedMeetings';
import { useBrand } from '../brand';
import { useCopy } from '../copy';
import { BrandMascot } from '../BrandMascot';
import { useViewer } from '../shell';
import { useTrack } from '../track';
import { useTourSteps } from '../tour';
import { TracksDrawer } from '../chrome';
import { BestBetTile } from '../common/BestBetTile';
import { addDays, datePath, isoDate, parseDatePath, sameDay } from '../lib/dates';
import { PREF, useStickyPref } from '../lib/prefs';
import { shortDay, localTime } from '../lib/format';
import { useDayAgreement } from './useDayAgreement';
import { useLiveRefresh } from '../lib/useLiveRefresh';
import { useUpcoming } from './useUpcoming';
import { LocksBanner } from './LocksBanner';
import { AskBanner } from './AskBanner';
import { Legend, MeetingGroups, TipsComingSoon, TodaySoFar, UpcomingTips } from './components';
import { TrackRecordSection } from './TrackRecordSection';
import {
  categoryRoi,
  categoryValueRoi,
  dayTotals,
  meetingHref,
  racesRun,
  toBestBet,
  type DailyBestBet,
  type HomeMeeting,
  nextRaceByMeeting,
} from './lib';
import './home.css';

const todayNoon = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);
};

/**
 * The home page body: upcoming tips, the day's best bets and scorecard, meetings
 * by track type, our track record and the tracks drawer. Route: / or /:dateString (YYYYMMDD).
 *
 * `hasTrackAccess` lets the app say which meetings the viewer's packages cover
 * (the old page used useSubscriptionAccess + the paywall date rule). Without it,
 * subscribers and private meetings are treated as open and everything else locked.
 */
export function HomeView({
  hasTrackAccess,
  promoPackage,
}: {
  hasTrackAccess?: (m: HomeMeeting) => boolean;
  /** The app's featured promo package card (admin "Featured Promo Package"), shown to signed-in members below the best bets. */
  promoPackage?: ReactNode;
} = {}) {
  const brand = useBrand();
  const copy = useCopy();
  const viewer = useViewer();
  const track = useTrack();
  const { dateString } = useParams<{ dateString?: string }>();
  const selected = parseDatePath(dateString) ?? todayNoon();
  const date = isoDate(selected);
  const isToday = sameDay(selected, new Date());
  const tomorrow = addDays(todayNoon(), 1);
  const authed = viewer.isAuthenticated;
  const analysisTypeId = viewer.analysisTypeId || 1;
  const LoggedOut = brand.loggedOutHome;

  const meetingsQ = useQuery(GET_MEETINGS_MINIMAL, { variables: { date, hasTips: true, analysisTypeId }, skip: !authed });
  const { upcoming, rows, locked } = useUpcoming();
  const nextRace = nextRaceByMeeting(rows);
  const tomorrowQ = useQuery(GET_MEETINGS_MINIMAL, {
    variables: { date: isoDate(tomorrow), hasTips: true, analysisTypeId },
    fetchPolicy: 'cache-first',
    skip: !authed || !isToday,
  });
  // Past meetings are always free to look back on, and nothing on a past day is still to be released
  const isPast = !isToday && selected < new Date();
  const unreleasedQ = useQuery(GET_UNRELEASED_MEETINGS, { variables: { date }, errorPolicy: 'all', skip: !authed || isPast });
  const tomorrowUnreleasedQ = useQuery(GET_UNRELEASED_MEETINGS, {
    variables: { date: isoDate(tomorrow) },
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
    skip: !authed || !isToday,
  });
  const bestQ = useQuery(GET_DAILY_BEST_BETS, { variables: { date, analysisTypeId }, fetchPolicy: 'cache-and-network', skip: !authed && !!LoggedOut });
  const promoQ = useQuery(GET_ACTIVE_PROMOTION, { errorPolicy: 'all' });
  const { agree, refetch: refetchAgree } = useDayAgreement(date, !authed);

  const [tw, setTw] = useStickyPref(PREF.tipsWonOnCards, 'on', ['on', 'off'] as const);
  const [welcome, setWelcome] = useState<string | null>(null);

  // A newly verified member lands here with a one-off welcome message.
  useEffect(() => {
    try {
      const msg = localStorage.getItem('verificationSuccessMessage');
      if (msg) {
        setWelcome(msg);
        localStorage.removeItem('verificationSuccessMessage');
      }
    } catch {
      // storage unavailable: no welcome message
    }
  }, []);

  // An active promotion gives a free trial even without visiting /promo; never overwrite an existing source.
  const promo = promoQ.data?.getActivePromotion;
  useEffect(() => {
    if (!promo?.source) return;
    try {
      if (!localStorage.getItem('promo_source')) localStorage.setItem('promo_source', promo.source);
    } catch {
      // storage unavailable: the trial falls back to the promo page
    }
  }, [promo?.source]);

  const meetings: HomeMeeting[] = useMemo(() => meetingsQ.data?.meetings ?? [], [meetingsQ.data]);

  // race day: keep the cards, Today so far, best tips and Prime Pick winners current until every race has a result
  useLiveRefresh(isToday && authed && meetings.some((m) => racesRun(m) < m.race_count), [meetingsQ.refetch, bestQ.refetch, refetchAgree]);

  // One page view per date, once auth has settled (as before).
  const tracked = useRef<string | null>(null);
  useEffect(() => {
    if (viewer.loading || tracked.current === date) return;
    tracked.current = date;
    track('page_view', {
      pageType: 'home',
      selectedDate: date,
      metadata: {
        isAuthenticated: authed,
        analysisTypeId,
        dateString: dateString || 'today',
        hasUpcomingRaces: upcoming.length > 0,
        meetingsCount: meetings.length,
      },
    });
  }, [viewer.loading, date, authed, analysisTypeId, dateString, upcoming.length, meetings.length, track]);

  // The tour explains the member home; a signed-out visitor sees the brand's marketing page, so no help button
  useTourSteps(authed ? [
    { target: 'upcoming', title: copy.upcomingTitle, body: 'The next races with a tip, across every track, soonest first. Tap Next to see more of the day.' },
    { target: 'locks', title: brand.locksName, body: 'Our strongest runners of the day, each rated a 55% chance or better, with how they have gone over the last 30 days.' },
    { target: 'bestbets', title: "Today's best free tips", body: 'Our strongest bets of the day and the value bet. Tap one for why we like it, our chance and our price.' },
    { target: 'sofar', title: 'Today so far', body: 'How the day is going: top pick strike rate and returns. Tap it for quinellas, trifectas, quaddies and more.' },
    { target: 'legend', title: 'The key', body: "What the bar colours and the markers mean. The Show on cards switch puts which tips won above every meeting's bars." },
    { target: 'groups', title: 'Track groups', body: "Meetings are grouped Metropolitan, Provincial and Country, with each group's return so far." },
    { target: 'meeting', title: 'A meeting', body: "Top pick and any-tip winners, one bar per race (green: a tip won, red: none, grey: not run), and the meeting's returns. Tap Tips & results to open it." },
    { target: 'drawer', title: 'Tracks', body: 'Jump to any meeting today from here, on every page.' },
  ] : []);

  const bb = bestQ.data?.dailyBestBets;
  const bestBets = bb && bb.totalBets > 0 && (
    <section className="ui-home-sec">
      <div className="ui-home-sh">
        <h2>{isToday ? copy.bestTipsToday : copy.bestTips}</h2>
      </div>
      <div className="ui-home-bb" data-tour="bestbets">
        {bb.bestBets.map((b: DailyBestBet, i: number) => (
          <BestBetTile key={`${b.raceId}-${b.tabNumber}`} bet={toBestBet(b, 'best', i)} />
        ))}
        {bb.valueBets.map((b: DailyBestBet, i: number) => (
          <BestBetTile key={`${b.raceId}-${b.tabNumber}-value`} bet={toBestBet(b, 'value', i)} />
        ))}
      </div>
    </section>
  );

  const upcomingStrip = <UpcomingTips rows={rows} locked={locked} signedOut={!authed} />;

  if (!authed) {
    return (
      <div className="ui-home">
        {brand.loggedOutUpcoming !== false && upcomingStrip}
        {LoggedOut ? <LoggedOut /> : bestBets}
      </div>
    );
  }

  const anyRun = meetings.some((m) => racesRun(m) > 0);
  const nextRaceTime = (m: HomeMeeting) => {
    const r = upcoming.find((u) => u.meetingId === m.meeting_id);
    return r ? localTime(r.time) : undefined;
  };
  const isLocked = (m: HomeMeeting) => !isPast && (hasTrackAccess ? !hasTrackAccess(m) : !(viewer.isSubscribed || m.is_private));
  const tomorrowCount = tomorrowQ.data?.meetings?.length ?? 0;
  const unreleased: string[] = unreleasedQ.data?.unreleasedMeetings?.tracks ?? [];
  // tomorrow's tips written but not out yet: the banner says they're coming rather than vanishing
  const tomorrowSoon = tomorrowCount === 0 ? (tomorrowUnreleasedQ.data?.unreleasedMeetings?.count ?? 0) : 0;
  const meetingsLoaded = !meetingsQ.loading && !meetingsQ.error && !!meetingsQ.data;

  return (
    <div className="ui-home" data-twall={tw === 'on'}>
      <AskBanner />
      {upcomingStrip}
      {welcome && (
        <div className="ui-welcome" role="status">
          <span>{welcome}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setWelcome(null)}>
            ×
          </button>
        </div>
      )}
      {bestBets}
      {promoPackage}
      <LocksBanner />
      {meetings.length > 0 && (
        <TodaySoFar totals={dayTotals(meetings)} roi={categoryRoi(meetings)} valueRoi={categoryValueRoi(meetings)} label={isToday ? copy.soFarToday : copy.soFar} agree={agree} />
      )}

      {(meetingsQ.loading || viewer.loading) && <p className="ui-home-note">Loading meetings…</p>}
      {meetingsQ.error && <p className="ui-home-note bad">Error loading meetings: {meetingsQ.error.message}</p>}
      {meetingsLoaded && meetings.length === 0 && !unreleasedQ.loading && (
        // a day ahead with nothing out yet says when to expect it; a past day just has none
        unreleased.length > 0 || (!isToday && !isPast) ? (
          <TipsComingSoon date={date} tracks={unreleased} />
        ) : (
          <div className="ui-home-note">
            <BrandMascot mood="bored" />
            <p>{copy.noMeetings}</p>
          </div>
        )
      )}

      {meetings.length > 0 && !viewer.loading && (
        <section className="ui-home-sec">
          <div className="ui-home-sh">
            <h2>{isToday && !anyRun ? copy.meetingsToday : copy.meetings}</h2>
          </div>
          <Legend
            showSwitch={anyRun}
            on={tw === 'on'}
            onToggle={() => {
              const next = tw === 'on' ? 'off' : 'on';
              setTw(next);
              track('ui_toggle', { control: 'show_won_on_cards', value: next });
            }}
          />
          <MeetingGroups meetings={meetings} isLocked={isLocked} nextRace={nextRaceTime} tour />
        </section>
      )}
      {meetings.length > 0 && !viewer.loading && unreleased.length > 0 && <TipsComingSoon date={date} tracks={unreleased} more />}

      <TrackRecordSection analysisTypeId={analysisTypeId} skip={!authed} />

      {isToday && (tomorrowCount > 0 || tomorrowSoon > 0) && (
        <div className="ui-tomorrow">
          <div>
            <b>{tomorrowCount > 0 ? copy.tomorrowReady : copy.tomorrowComingSoon}</b>
            <div className="ui-home-muted">
              {tomorrowCount || tomorrowSoon} meetings · {shortDay(isoDate(tomorrow))}
            </div>
          </div>
          <Link to={`/${datePath(tomorrow)}`} className="ui-home-btn quiet">
            Preview
          </Link>
        </div>
      )}

      {meetings.length > 0 && (
        <TracksDrawer
          title={isToday ? "Today's tracks" : 'Tracks'}
          subtitle={`${meetings.length} meetings · ${shortDay(date)}`}
          meetings={meetings.map((m) => ({
            key: m.meeting_id,
            name: m.track.name,
            state: m.track.state,
            type: m.track.type,
            href: meetingHref(m),
            next: nextRace.get(m.meeting_id),
          }))}
        />
      )}
    </div>
  );
}
